import { AROMA_FAMILY_ORDER, flavourColour, specAroma, topAromaFamilies, type Ingredient, type Spec } from "@baros/core";

/** Families under this share are left off the chips; the bar still draws them. */
const CHIP_MIN_SHARE = 0.05;

/**
 * What the drink smells of, rolled up from its build (`specAroma`, #127): a
 * thin stacked bar of every family present, in the flavour wheel's colours,
 * and chips for the three it leans on most.
 *
 * Says so when the picture is incomplete — lines whose ingredient the
 * library does not hold are left out of the sums — and says nothing about
 * water, ice and dilution, which have no aroma to miss.
 */
export function SpecAroma(p: {
  readonly spec: Pick<Spec, "lines">;
  readonly ingredients: ReadonlyMap<string, Ingredient>;
}) {
  const aroma = specAroma(p.spec, p.ingredients);
  const all = topAromaFamilies(aroma.shares, AROMA_FAMILY_ORDER.length);
  const top = topAromaFamilies(aroma.shares, 3, CHIP_MIN_SHARE);
  const unknown = aroma.unknownIngredientIds.length;
  const coarse = aroma.familyOnlyIngredientIds.length;

  return (
    <div>
      <div className="label" style={{ marginBottom: 8 }}>Aroma</div>
      {all.length === 0 ? (
        <span className="muted" style={{ fontSize: 13 }}>
          {unknown > 0 ? "No aroma: none of this build's ingredients are in the library." : "Nothing in this build carries an aroma family."}
        </span>
      ) : (
        <>
          <div
            className="aroma-bar"
            role="img"
            aria-label={`Aroma: ${all.map((f) => `${f.family} ${Math.round(f.share * 100)}%`).join(", ")}`}
          >
            {all.map((f) => (
              <span key={f.family} style={{ flexGrow: f.share, background: flavourColour(f.family) }} title={`${f.family} ${Math.round(f.share * 100)}%`} />
            ))}
          </div>
          <div className="aroma-chips">
            {top.map((f) => (
              <span key={f.family} className="label aroma-chip">
                <span className="dot" style={{ background: flavourColour(f.family) }} />
                {f.family} <span className="mono">{Math.round(f.share * 100)}%</span>
              </span>
            ))}
          </div>
        </>
      )}
      {(unknown > 0 || coarse > 0) && (
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 8 }}>
          {unknown > 0 && `Partial: ${unknown} line${unknown === 1 ? "" : "s"} not in the library, left out. `}
          {coarse > 0 && `${coarse} ingredient${coarse === 1 ? " has" : "s have"} only a main family until linked to the flavour database.`}
        </div>
      )}
    </div>
  );
}
